import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CommunityAssessmentData, StakeholderInfo } from '@/types/entities/clients/assessment';
import { Users, UserPlus, Trash2, Crown, MessageCircle, Clock } from 'lucide-react';

interface StakeholderMappingProps {
  data: Partial<CommunityAssessmentData>;
  onUpdate: (data: Partial<CommunityAssessmentData>) => void;
  tier: 'silver' | 'gold' | 'custom';
}

export const Step_5_StakeholderMapping: React.FC<StakeholderMappingProps> = ({
  data,
  onUpdate,
}) => {
  const [newStakeholder, setNewStakeholder] = useState<StakeholderInfo>({
    title: '',
    name: '',
    email: '',
    role: 'other',
  });

  const updateDecisionMaking = (field: string, value: string | number | string[]) => {
    onUpdate({
      ...data,
      decisionMakingProcess: {
        ...data.decisionMakingProcess,
        [field]: value,
      },
    });
  };

  const updateResidentInputMethods = (method: string, checked: boolean) => {
    const currentMethods = data.decisionMakingProcess?.residentInputMethods || [];
    const updated = checked
      ? [...currentMethods, method]
      : currentMethods.filter((m) => m !== method);
    updateDecisionMaking('residentInputMethods', updated);
  };

  const addStakeholder = () => {
    if (!newStakeholder.title || !newStakeholder.name) return;

    const currentStakeholders = data.keyStakeholders || [];
    onUpdate({
      ...data,
      keyStakeholders: [...currentStakeholders, newStakeholder],
    });
    setNewStakeholder({ title: '', name: '', email: '', role: 'other' });
  };

  const removeStakeholder = (index: number) => {
    const currentStakeholders = data.keyStakeholders || [];
    onUpdate({
      ...data,
      keyStakeholders: currentStakeholders.filter((_, i) => i !== index),
    });
  };

  const updateStakeholder = (index: number, field: keyof StakeholderInfo, value: string) => {
    const currentStakeholders = data.keyStakeholders || [];
    const updated = currentStakeholders.map((stakeholder, i) =>
      i === index ? { ...stakeholder, [field]: value } : stakeholder,
    );
    onUpdate({
      ...data,
      keyStakeholders: updated,
    });
  };

  const residentInputOptions = [
    'Annual meetings',
    'Board meetings (open to residents)',
    'Online surveys',
    'Email feedback',
    'Community forums/discussion boards',
    'Social media groups',
    'Suggestion boxes',
    'Town halls/special meetings',
    'Committee participation',
    'No formal input methods',
  ];

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'decision_maker':
        return <Crown className="w-4 h-4 text-yellow-600" />;
      case 'influencer':
        return <MessageCircle className="w-4 h-4 text-blue-600" />;
      case 'supporter':
        return <Users className="w-4 h-4 text-green-600" />;
      default:
        return <Users className="w-4 h-4 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Stakeholder Mapping & Decision Making
        </h3>
        <p className="text-gray-600">
          Understanding who's involved in decisions helps us plan the right engagement strategy
        </p>
      </div>

      <div className="space-y-6">
        {/* Decision Making Process */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Decision Making Process
            </CardTitle>
            <CardDescription>How are major decisions made in your community?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="boardSize">Board Size</Label>
                <Input
                  id="boardSize"
                  type="number"
                  value={data.decisionMakingProcess?.boardSize || ''}
                  onChange={(e) => updateDecisionMaking('boardSize', parseInt(e.target.value) || 0)}
                  placeholder="e.g., 5"
                  min="1"
                  max="20"
                />
              </div>

              <div>
                <Label htmlFor="meetingFrequency">Board Meeting Frequency</Label>
                <Select
                  value={data.decisionMakingProcess?.meetingFrequency || ''}
                  onValueChange={(value) => updateDecisionMaking('meetingFrequency', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="as_needed">As needed</SelectItem>
                    <SelectItem value="irregular">Irregular schedule</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="votingThreshold">Voting Threshold for Decisions</Label>
                <Select
                  value={data.decisionMakingProcess?.votingThreshold || ''}
                  onValueChange={(value) => updateDecisionMaking('votingThreshold', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select threshold" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simple_majority">Simple majority (50% + 1)</SelectItem>
                    <SelectItem value="supermajority_60">Supermajority (60%)</SelectItem>
                    <SelectItem value="supermajority_67">Supermajority (67%)</SelectItem>
                    <SelectItem value="supermajority_75">Supermajority (75%)</SelectItem>
                    <SelectItem value="unanimous">Unanimous</SelectItem>
                    <SelectItem value="varies">Varies by decision type</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="majorDecisionTimeline">Typical Timeline for Major Decisions</Label>
                <Select
                  value={data.decisionMakingProcess?.majorDecisionTimeline || ''}
                  onValueChange={(value) => updateDecisionMaking('majorDecisionTimeline', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select timeline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="same_meeting">Same meeting discussion</SelectItem>
                    <SelectItem value="one_month">1 month</SelectItem>
                    <SelectItem value="two_months">2-3 months</SelectItem>
                    <SelectItem value="quarterly">Quarterly cycle</SelectItem>
                    <SelectItem value="annual">Annual process</SelectItem>
                    <SelectItem value="varies">Varies significantly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="budgetApprovalProcess">Budget Approval Process</Label>
              <Textarea
                id="budgetApprovalProcess"
                value={data.decisionMakingProcess?.budgetApprovalProcess || ''}
                onChange={(e) => updateDecisionMaking('budgetApprovalProcess', e.target.value)}
                placeholder="e.g., Board proposes budget, 30-day resident review period, annual meeting approval..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Resident Input Methods */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resident Input & Participation</CardTitle>
            <CardDescription>
              How do residents currently provide input on community decisions?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {residentInputOptions.map((method) => (
                <div key={method} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`input-${method}`}
                    checked={
                      data.decisionMakingProcess?.residentInputMethods?.includes(method) || false
                    }
                    onChange={(e) => updateResidentInputMethods(method, e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor={`input-${method}`} className="text-sm">
                    {method}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Key Stakeholders */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Key Stakeholders
            </CardTitle>
            <CardDescription>
              Who are the key people involved in community decisions? (Include yourself and other
              board members)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Existing Stakeholders */}
            {data.keyStakeholders && data.keyStakeholders.length > 0 && (
              <div className="space-y-3">
                {data.keyStakeholders.map((stakeholder, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {getRoleIcon(stakeholder.role || 'other')}
                        <span className="font-medium">{stakeholder.title}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeStakeholder(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={stakeholder.name}
                          onChange={(e) => updateStakeholder(index, 'name', e.target.value)}
                          placeholder="Full name"
                        />
                      </div>
                      <div>
                        <Label>Email (optional)</Label>
                        <Input
                          type="email"
                          value={stakeholder.email || ''}
                          onChange={(e) => updateStakeholder(index, 'email', e.target.value)}
                          placeholder="email@example.com"
                        />
                      </div>
                      <div>
                        <Label>Role in Decisions</Label>
                        <Select
                          value={stakeholder.role || 'other'}
                          onValueChange={(value) => updateStakeholder(index, 'role', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="decision_maker">Decision Maker</SelectItem>
                            <SelectItem value="influencer">Key Influencer</SelectItem>
                            <SelectItem value="supporter">Supporter</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Stakeholder */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <UserPlus className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">Add Stakeholder</span>
              </div>
              <div className="grid md:grid-cols-4 gap-3">
                <div>
                  <Label>Title/Position</Label>
                  <Input
                    value={newStakeholder.title}
                    onChange={(e) =>
                      setNewStakeholder({ ...newStakeholder, title: e.target.value })
                    }
                    placeholder="e.g., Board President"
                  />
                </div>
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newStakeholder.name}
                    onChange={(e) => setNewStakeholder({ ...newStakeholder, name: e.target.value })}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select
                    value={newStakeholder.role}
                    onValueChange={(value) =>
                      setNewStakeholder({
                        ...newStakeholder,
                        role: value as 'decision_maker' | 'influencer' | 'supporter' | 'other',
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="decision_maker">Decision Maker</SelectItem>
                      <SelectItem value="influencer">Key Influencer</SelectItem>
                      <SelectItem value="supporter">Supporter</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={addStakeholder}
                    disabled={!newStakeholder.title || !newStakeholder.name}
                    className="w-full"
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stakeholder Summary */}
      {data.keyStakeholders && data.keyStakeholders.length > 0 && (
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <h4 className="font-semibold text-purple-800 mb-2">Engagement Strategy</h4>
              <p className="text-sm text-purple-700 mb-3">
                We'll prepare a customized presentation for your {data.keyStakeholders.length} key
                stakeholders
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {data.keyStakeholders.map((stakeholder, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs flex items-center gap-1"
                  >
                    {getRoleIcon(stakeholder.role || 'other')}
                    {stakeholder.title}
                  </span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          <strong>Why stakeholder mapping matters:</strong>
        </p>
        <ul className="text-sm text-blue-700 mt-2 space-y-1">
          <li>• Customize our presentation for the right decision makers</li>
          <li>• Plan appropriate training sessions for each role</li>
          <li>• Design approval workflows that match your current process</li>
          <li>• Ensure all key voices are heard during implementation planning</li>
          <li>• Identify potential champions who can help drive adoption</li>
        </ul>
      </div>
    </div>
  );
};
